import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, AlertTriangle, Info, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const InfoAlerts = () => {
  const { language } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  // Auto-rotate carousel every 5 seconds (when not paused)
  useEffect(() => {
    if (alerts.length <= 1 || isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % alerts.length);
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [alerts.length, isPaused]);

  const fetchAlerts = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/info-alerts?active_only=true`);
      const data = await response.json();
      setAlerts(data);
    } catch (error) {
      console.error('Error fetching info alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + alerts.length) % alerts.length);
    setIsPaused(true); // Pause when manually navigating
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % alerts.length);
    setIsPaused(true); // Pause when manually navigating
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-6 h-6" />;
      case 'important':
        return <AlertCircle className="w-6 h-6" />;
      default:
        return <Info className="w-6 h-6" />;
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'warning':
        return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-400 text-yellow-900';
      case 'important':
        return 'bg-gradient-to-r from-red-50 to-red-100 border-red-400 text-red-900';
      default:
        return 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-400 text-blue-900';
    }
  };

  if (loading || alerts.length === 0) {
    return null;
  }

  const currentAlert = alerts[currentIndex];

  return (
    <section className="py-6 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className={`relative border-2 rounded-lg p-6 shadow-lg ${getTypeStyles(currentAlert.type)}`}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Buttons */}
          {alerts.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-md transition-colors z-10"
                aria-label="Previous alert"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-md transition-colors z-10"
                aria-label="Next alert"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              
              {/* Pause/Play Button */}
              <button
                onClick={togglePause}
                className="absolute right-14 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-md transition-colors z-10"
                aria-label={isPaused ? 'Resume auto-play' : 'Pause auto-play'}
                title={isPaused ? 'Fortsetzen' : 'Pausieren'}
              >
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </button>
            </>
          )}

          {/* Content */}
          <div className="flex items-start space-x-4 px-8">
            <div className="flex-shrink-0 mt-1">
              {getTypeIcon(currentAlert.type)}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-2">
                {language === 'de' ? currentAlert.title_de : currentAlert.title_en}
              </h3>
              <p className="text-base leading-relaxed whitespace-pre-line">
                {language === 'de' ? currentAlert.content_de : currentAlert.content_en}
              </p>
            </div>
          </div>

          {/* Dots Indicator */}
          {alerts.length > 1 && (
            <div className="flex justify-center space-x-2 mt-4">
              {alerts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? 'bg-gray-800 w-6'
                      : 'bg-gray-400 hover:bg-gray-600'
                  }`}
                  aria-label={`Go to alert ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InfoAlerts;
